/**
 * useProductBreadcrumbs Hook
 * 
 * Product için dinamik breadcrumb oluşturan hook
 * UrlBuilder kullanarak varsayılan dil için prefix olmadan URL oluşturur
 */

import { useState, useEffect } from 'react';
import { buildCategoryUrl, getProductTypeBaseUrl } from '@/lib/url-helpers';
import type { PageUrlMap } from '@/types/blog';

export interface BreadcrumbItem {
    title: string;
    link: string;
    active: boolean;
}

export interface UseProductBreadcrumbsReturn {
    breadcrumbs: BreadcrumbItem[];
    isLoading: boolean;
    error: string | null;
}

export function useProductBreadcrumbs(
    product: any,
    language: string = 'en',
    enabled: boolean = true,
    pageUrls?: PageUrlMap,
    defaultLanguage: string = 'en'
): UseProductBreadcrumbsReturn {
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
    const [isLoading] = useState(false);
    const [error] = useState<string | null>(null);

    useEffect(() => {
        if (!product || !enabled) {
            setBreadcrumbs([]);
            return;
        }

        const items: BreadcrumbItem[] = [];

        const homeLink = defaultLanguage === language ? '/' : `/${language}`;
        items.push({
            title: language === 'tr' ? 'Ana Sayfa' : 'Home',
            link: homeLink,
            active: false,
        });

        const productType = product.type || product.product_type;
        
        // Product type yoksa breadcrumb oluşturma
        if (!productType) {
            setBreadcrumbs(items);
            return;
        }

        let categoryTitle = '';
        let categoryLink = '';

        // Product type'ı normalize et (yacht_routes -> yacht-routes gibi)
        const normalizedProductType = productType.replace(/_/g, '-');

        // Category varsa category bilgilerini kullan
        if (product.categories && product.categories.length > 0) {
            const firstCategory = product.categories[0];
            categoryTitle = firstCategory.name || '';
            
            if (pageUrls) {
                categoryLink = buildCategoryUrl(normalizedProductType, firstCategory.slug || '', pageUrls, language, defaultLanguage);
            } else {
                const baseUrl = getProductTypeBaseUrl(normalizedProductType, pageUrls, language, defaultLanguage);
                if (baseUrl) {
                    categoryLink = firstCategory.slug 
                        ? `${baseUrl}/${firstCategory.slug}`.replace(/\/+/g, '/')
                        : baseUrl;
                }
            }
        } else {
            // Category yoksa sadece base URL kullan
            if (pageUrls) {
                categoryLink = getProductTypeBaseUrl(normalizedProductType, pageUrls, language, defaultLanguage) || '';
            }
        }

        // Category title yoksa ve link varsa, link'ten title çıkarmaya çalış
        if (!categoryTitle && categoryLink) {
            // Link'ten son segment'i al (örn: /private-tours -> private-tours)
            const segments = categoryLink.split('/').filter(Boolean);
            if (segments.length > 0) {
                const lastSegment = segments[segments.length - 1];
                // Basit title oluştur (slug'ı capitalize et)
                categoryTitle = lastSegment
                    .split('-')
                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
        }

        // Eğer hala title yoksa, product type'ı kullan
        if (!categoryTitle) {
            categoryTitle = normalizedProductType
                .split('-')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }

        items.push({
            title: categoryTitle,
            link: categoryLink,
            active: false,
        });

        if (product.name) {
            items.push({
                title: product.name,
                link: '#',
                active: true,
            });
        }

        setBreadcrumbs(items);
    }, [product?.id, product?.name, product?.type, product?.product_type, product?.categories, language, enabled, pageUrls, defaultLanguage]);

    return {
        breadcrumbs,
        isLoading,
        error,
    };
}

